/* global setInterval, clearInterval */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import * as ozon from "@/api/ozonSupply";
import { ACTIVE_SLOT_WATCHES, dirKey, slotWatchEventsKey, slotWatchKey } from "./ozonQueries";

/** Опрос активного мониторинга: тик раз в минуту, экрану хватает раза в 30 секунд. */
export const WATCH_POLL_MS = 30_000;

/** Мониторинг мест поставки: чтение с опросом, постановка и снятие.
 *
 *  ⚠️ Ответ ручек — ПОЛНЫЙ мониторинг: кладём его в кэш, а поставку и отметки реестра
 *  перечитываем — постановка меняет пункт сдачи направления, а реестр показывает «на мониторинге».
 */
export function useSlotWatch(shipmentId: number, enabled: boolean) {
  const qc = useQueryClient();
  const watch = useQuery({
    queryKey: slotWatchKey(shipmentId),
    queryFn: () => ozon.getSlotWatch(shipmentId),
    enabled,
    refetchInterval: (q) => (q.state.data?.status === "active" ? WATCH_POLL_MS : false),
  });

  // ⚠️ Опрос принёс «уже не active» (заявка создана / истёк / снят в другой вкладке): наблюдатель мог
  // создать заявку и сменить пункт сдачи — направление и список активных перечитываем, иначе экран
  // держит старую стадию, а замок поставки снимается неверно
  const status = watch.data?.status;
  const prev = useRef(status);
  useEffect(() => {
    if (prev.current === "active" && status !== undefined && status !== "active") {
      void qc.invalidateQueries({ queryKey: dirKey(shipmentId) });
      void qc.invalidateQueries({ queryKey: ACTIVE_SLOT_WATCHES });
    }
    prev.current = status;
  }, [status, qc, shipmentId]);

  const settle = (w: ozon.SlotWatchView): void => {
    qc.setQueryData(slotWatchKey(shipmentId), w);
    void qc.invalidateQueries({ queryKey: slotWatchEventsKey(w.id) });
    void qc.invalidateQueries({ queryKey: ACTIVE_SLOT_WATCHES });
    void qc.invalidateQueries({ queryKey: dirKey(shipmentId) });
  };

  // ошибка постановки/снятия могла прийти уже после смены состояния на бэкенде (409: «уже на
  // мониторинге» / «не на мониторинге») — перечитываем мониторинг и направление, ошибку показываем
  const refetchAfterError = (): void => {
    void qc.invalidateQueries({ queryKey: slotWatchKey(shipmentId) });
    void qc.invalidateQueries({ queryKey: dirKey(shipmentId) });
  };

  // ⚠️ ошибки — НЕ тостом: их показывает сам диалог (409/422 с причиной по-русски), и он остаётся
  // открытым, чтобы поправить ввод
  const start = useMutation({
    mutationFn: (body: ozon.SlotWatchStart) => ozon.startSlotWatch(shipmentId, body),
    onSuccess: (w) => {
      settle(w);
      toast.success(`${w.region}: поставлено на мониторинг мест · ${w.drop_off_name}`);
    },
    onError: refetchAfterError,
  });

  const stop = useMutation({
    mutationFn: () => ozon.stopSlotWatch(shipmentId),
    onSuccess: (w) => {
      settle(w);
      toast.success(`${w.region}: мониторинг мест снят`);
    },
    onError: refetchAfterError,
  });

  return { watch, start, stop };
}

/** Текущее время, тикающее раз в `ms`: предупреждение «наблюдатель не отвечает» обязано появиться
 *  на открытой странице, даже если опрос приносит тот же ответ и экран не перерисовывается. */
export function useNow(ms: number, on: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!on) return;
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms, on]);
  return now;
}
